/* eslint-disable no-case-declarations */
/* eslint-disable max-len */
/* eslint-disable no-param-reassign */
import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { MetadataContent } from '@jutro/legacy/uiconfig';
import _ from 'lodash';
import metadata from './MLHOBusinessPursuitsLiabilityInsComponent.metadata.json5';
import messages from './MLHOBusinessPursuitsLiabilityInsComponent.messages';
import appConfig from 'app-config';
import styles from './MLHOBusinessPursuitsLiabilityInsComponent.module.scss';
import { useTranslator } from '@jutro/locale';

const BUSINESS_PURSUITS_LIABILITY_TYPE = 'businessPursuitsLiabilityInsEmp';

// Used for HO Business Pursuits Liability Insured Employees Within Home
function MLHOBusinessPursuitsLiabilityInsComponent(props) {
    const {
        items,
        defaultItem,
        schedItemType,
        readOnly,
        loading,
        parentRemoveItemHandler,
        resetChosenTermAndChosenTermValue,
        formatOptionAvailableVals,
        onCovChange
    } = props;

    const isQnB = appConfig.portalCode === 'QB';
    const translator = useTranslator();

    const isBusinessPursuitsType = schedItemType === BUSINESS_PURSUITS_LIABILITY_TYPE;

    const getDefaultScheduleItem = useCallback(() => {
        return _.omit(defaultItem, ['fixedID']);
    }, [defaultItem, resetChosenTermAndChosenTermValue]);

    const onAddItem = useCallback(() => {
        const defaultItemVal = getDefaultScheduleItem();
        items.push(defaultItemVal);
        onCovChange(undefined, undefined, undefined, true);
    }, [getDefaultScheduleItem, items, onCovChange]);

    const onRemoveItem = useCallback((evt) => {
        parentRemoveItemHandler(evt.index, schedItemType);
    }, [parentRemoveItemHandler, schedItemType]);

    const dropdownChanged = useCallback((selectedOption, item) => {
        item.chosenTerm = selectedOption;
        item.updated = true;
        onCovChange(undefined,undefined,undefined,true);
    }, [onCovChange]);

    const onBusinessNameChange = useCallback((value, item) => {
        item.businessName = value;
        onCovChange(undefined,undefined,undefined,false);
    });

    const onIsInsuredSoleOwnerChange = useCallback((value, item) => {
        item.isInsuredSoleOwner = value;
        onCovChange(undefined,undefined,undefined,false);
    })

    const generateItemOverrides = useCallback(() => {
        const itemOverrides = items.map((item, itemIndex) => {
            const selectedInsured = item.insured?.chosenTerm || item.insured?.chosenTermValue;
            const insuredDisplayName =
                item.insured?.options?.find(
                    option =>
                        option.code === selectedInsured ||
                        option.optionCode_ML === selectedInsured
                )?.name || item.insured?.chosenTermValue;

            return {
                [`actionRow${itemIndex}`]: {
                    visible: !readOnly,
                    className: loading ? 'cell disabled' : 'cell'
                },
                // Business Name
                [`businessNameRow${itemIndex}`]: {
                    visible: isBusinessPursuitsType
                },
                [`businessNameInputDiv${itemIndex}`]: {
                    visible: !readOnly && isBusinessPursuitsType
                },
                [`businessNameInput${itemIndex}`]: {
                    visible: isBusinessPursuitsType,
                    value: item.businessName,
                    disabled: loading,
                    path: item.businessName,
                    onValueChange: (value) => onBusinessNameChange(value, item),
                    label: isQnB ? translator(messages.businessName) : '',
                    labelClassName : isQnB ? 'businessLabelStyle' : ''
                },
                [`businessNameReadonlyMobileSpan${itemIndex}`]: {
                    visible: readOnly && isBusinessPursuitsType
                },
                [`businessNameReadOnlyContent${itemIndex}`]: {
                    visible: readOnly && isBusinessPursuitsType
                },
                // Insured
                [`insuredSelectDiv${itemIndex}`]: {
                    visible: !readOnly,
                    className: isQnB ? 'insuredStyle' : 'select'
                },
                [`insuredDropdown${itemIndex}`]: {
                    value: item.insured && item.insured.chosenTerm,
                    availableValues: formatOptionAvailableVals(_.get(item, 'insured.options', [])),
                    disabled: loading,
                    path: item.insured,
                    onValueChange: dropdownChanged,
                    label: isQnB ? translator(messages.insured) : '',
                    labelClassName : isQnB ? 'valuableLabelStyle' : ''
                },
                [`insuredReadonlyMobileSpan${itemIndex}`]: {
                    visible: readOnly
                },
                [`insuredReadOnlyContent${itemIndex}`]: {
                    visible: readOnly,
                    content: insuredDisplayName
                },
                // Occupation
                [`occupationSelectDiv${itemIndex}`]: {
                    visible: !readOnly,
                    className: isQnB ? 'occupationStyle' : 'select'
                },
                [`occupationDropdown${itemIndex}`]: {
                    value: item.occupation && item.occupation.chosenTerm,
                    availableValues: formatOptionAvailableVals(_.get(item, 'occupation.options', [])),
                    disabled: loading,
                    path: item.occupation,
                    onValueChange: dropdownChanged,
                    label: isQnB ? translator(messages.occupation) : '',
                    labelClassName : isQnB ? 'valuableLabelStyle' : ''
                },
                [`occupationReadonlyMobileSpan${itemIndex}`]: {
                    visible: readOnly
                },
                [`occupationReadOnlyContent${itemIndex}`]: {
                    visible: readOnly
                },
                // Is Insured Sol Owner
                [`isInsuredSoleOwnerRow${itemIndex}`]: {
                    visible: isBusinessPursuitsType
                },
                [`isInsuredSoleOwnerInputDiv${itemIndex}`]: {
                    visible: !readOnly && isBusinessPursuitsType
                },
                [`isInsuredSoleOwnerInput${itemIndex}`]: {
                    visible: isBusinessPursuitsType,
                    value: item.isInsuredSoleOwner,
                    disabled: loading,
                    path: item.isInsuredSoleOwner,
                    onValueChange: (value) => onIsInsuredSoleOwnerChange(value, item),
                    label: isQnB ? translator(messages.isInsuredSoleOwner) : '',
                    labelClassName : isQnB ? 'businessLabelStyle' : ''
                },
                [`isInsuredSoleOwnerReadonlyMobileSpan${itemIndex}`]: {
                    visible: readOnly && isBusinessPursuitsType
                },
                [`isInsuredSoleOwnerReadOnlyContent${itemIndex}`]: {
                    visible: readOnly && isBusinessPursuitsType
                },
            };
        });

        return Object.assign({}, ...itemOverrides);
    }, [dropdownChanged, formatOptionAvailableVals, isBusinessPursuitsType, items, loading, readOnly]);

    const overrides = {
        stackTableDiv: {
            className: readOnly ? 'stack-table' : 'stack-table plain'
        },
        actionHeader: {
            visible: !readOnly
        },
        tableRowIterable: {
            data: items
        },
        bottomButtonContainer: {
            visible: !readOnly
        },
        addItemBtn: {
            visible: !readOnly,
            disabled: loading
        },
        ...generateItemOverrides()
    };


    const resolvers = {
        resolveCallbackMap: {
            onAddItem,
            onRemoveItem
        },
        resolveClassNameMap: styles
    };

    return (
        <MetadataContent
            uiProps={metadata.componentContent}
            overrideProps={overrides}
            {...resolvers} />
    );
}

MLHOBusinessPursuitsLiabilityInsComponent.propTypes = {
    items: PropTypes.arrayOf(PropTypes.shape({})),
    schedItemType: PropTypes.string,
    defaultItem: PropTypes.shape({}),
    readOnly: PropTypes.bool,
    loading: PropTypes.bool,
    parentRemoveItemHandler: PropTypes.func,
    resetChosenTermAndChosenTermValue: PropTypes.func,
    onCovChange: PropTypes.func
};

export default MLHOBusinessPursuitsLiabilityInsComponent;
